import BaseCache from '../base/cache';
import { RequestOption } from '../base/define';
import { ROLES } from '../team/define';
import {
  User,
  UserDetailRequest,
  UserOrderRequest,
  UserCourse,
  YouDaoUser,
  PandaUserLoginInfo,
  LoginRequest,
  PandaUserLoginRequest,
  Address,
  UserInfo,
  Integral,
  TeamInfo,
  SmsRequest,
  Member,
  UpdateMemberRequest,
  UserLearnReportTotalStatistic,
  UserLearnReportDailyUnit,
  UserLearnReportDayList,
  AccountCancellationRequest,
  UserContact,
} from './define';
import { Order } from '../order/define';
import { Pagination, PaginationRequest } from '../shared/define/Pagination';
import config from './config';

export default class UserCache extends BaseCache {
  constructor() {
    super(config);
  }

  static isLogin() {
    return window.PandaNamespace?.isLogin;
  }

  static isPartialAccess() {
    return window.PandaNamespace.isPartialAccess;
  }

  static checkPermission(roles: ROLES[], targetRoles: ROLES[]) {
    return roles.filter(
      (role: ROLES) => targetRoles.findIndex(
        (targetRoleItem) => role === targetRoleItem,
      ) !== -1,
    ).length > 0;
  }

  static hasPermission = (targetRoles: ROLES[]) => {
    if (!window.PandaNamespace?.teamInfo) {
      return false;
    }
    const { roles } = window.PandaNamespace.teamInfo;
    if (!roles || !roles.length) {
      return false;
    }
    return UserCache.checkPermission(roles, targetRoles);
  };

  loginYouDao() {
    return this.doSendRequest('login-youdao', {
      params: {
        app: 'web',
        product: 'DICT',
        version: '2.0',
        tp: 'urs-phone-web',
        cf: 7,
        show: true,
        newjson: true,
      },
    }) as Promise<YouDaoUser>;
  }

  getYouDaoLoginInfo() {
    return this.doSendRequest('get-youdao-login-info', {
      params: {
        cf: 7,
        newjson: true,
        product: 'DICT',
        version: '2.0',
      },
    }) as Promise<YouDaoUser>;
  }

  logoutYouDao(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('logout-youdao', option) as Promise<undefined>;
  }

  loginPanda(option: RequestOption<PandaUserLoginRequest, undefined>) {
    return this.doSendRequest('login-panda', option) as Promise<PandaUserLoginInfo>;
  }

  logoutPanda(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('logout-panda', option) as Promise<undefined>;
  }

  accountCancellation(option: RequestOption<AccountCancellationRequest, boolean>) {
    return this.doSendRequest('account-cancellation', option) as Promise<boolean>;
  }

  getUserList(option: RequestOption<undefined, PaginationRequest>) {
    return this.doSendRequest('get-user-list', option) as Promise<Pagination<User>>;
  }

  getUserInfo(option: RequestOption<undefined, LoginRequest>) {
    return this.doSendRequest('get-user-info', option) as Promise<UserInfo>;
  }

  getUserInfoPure(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('get-user-info-pure', option) as Promise<UserInfo>;
  }

  getUserDetail(option: RequestOption<undefined, UserDetailRequest>) {
    return this.doSendRequest('get-user-detail', option) as Promise<User>;
  }

  getUserCourseList(option: RequestOption<undefined, PaginationRequest>) {
    return this.doSendRequest('get-user-course-list', option) as Promise<Pagination<UserCourse>>;
  }

  getUserOrderList(option: RequestOption<undefined, PaginationRequest>) {
    return this.doSendRequest('get-user-order-list', option) as Promise<Pagination<Order>>;
  }

  getUserOrderArray(option: RequestOption<undefined, UserOrderRequest>) {
    return this.doSendRequest('get-user-order-array', option) as Promise<Array<Order>>;
  }

  getUserBindingOrderList(option: RequestOption<undefined, UserOrderRequest>) {
    return this.doSendRequest('get-user-binding-order-list', option) as Promise<Array<Order>>;
  }

  getUserAddress(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('get-user-address', option) as Promise<Address>;
  }

  getUserIntegral(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('get-user-integral', option) as Promise<Integral>;
  }

  updateUserAddress(option: RequestOption<Address, undefined>) {
    return this.doSendRequest('update-user-address', option) as Promise<Address>;
  }

  export(option: RequestOption<undefined, undefined>) {
    this.doExport(option, 'export-list', 'export-list-process');
  }

  // 获取用户团队信息
  getTeamInfo(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('get-team-info', option) as Promise<TeamInfo>;
  }

  getTeacherInfo(option: RequestOption<undefined, {teacherId: number}>) {
    return this.doSendRequest('get-teacher-info', option) as Promise<TeamInfo>;
  }

  // 判断是否需要绑定常用手机号
  getSmsCodeFlag(option: RequestOption<undefined, undefined>) {
    return this.doSendRequest('get-sms-code-flag', option) as Promise<boolean>;
  }

  // 发生验证码
  sendSms(option: RequestOption<SmsRequest, undefined>) {
    return this.doSendRequest('send-sms', option) as Promise<boolean>;
  }

  getUserMemberList(option: RequestOption<undefined, PaginationRequest>) {
    return this.doSendRequest('get-user-member-list', option) as Promise<Pagination<Member>>;
  }

  onOrOffVip(option: RequestOption<UpdateMemberRequest, undefined>) {
    return this.doSendRequest('on-or-off-vip', option) as Promise<boolean>;
  }

  getUserLearnReportTotalStatistic(option: RequestOption<undefined, {uid: number}>) {
    return this.doSendRequest('get-user-learn-report-total-statistic', option) as Promise<UserLearnReportTotalStatistic>;
  }

  getUserLearnReportDayList(option: RequestOption<undefined, PaginationRequest>) {
    return this.doSendRequest('get-user-learn-report-day-list', option) as Promise<Pagination<UserLearnReportDayList>>;
  }

  getUserLearnReportDailyUnitList(option: RequestOption<undefined, {uid: number; day: number}>) {
    return this.doSendRequest('get-user-learn-report-daily-unit-list', option) as Promise<UserLearnReportDailyUnit[]>;
  }

  getUserContactList(option: RequestOption<undefined, {uid: number}>) {
    return this.doSendRequest('get-user-contact-list', option) as Promise<UserContact[]>;
  }

  addUserContact(option: RequestOption<{uid: number; content: string}, undefined>) {
    return this.doSendRequest('add-user-contact', option) as Promise<UserContact>;
  }
}
